import type { CSSProperties } from "react";
import { cn } from "../lib/utils";
import { PersonaImageInfo, PoseOrientation } from "@um/contracts";
import { paperEdgeFilter, usePuppetStick } from "../characters/puppetStick";

/** A pale birch craft stick with a little grain. */
const STICK_STYLE: CSSProperties = {
  position: "absolute",
  width: "7%",
  marginLeft: "-3.5%",
  borderRadius: "999px 999px 0 0",
  background:
    "repeating-linear-gradient(177deg, rgba(122,84,44,0) 0 7px, rgba(122,84,44,0.12) 7px 9px, rgba(122,84,44,0) 9px 19px), linear-gradient(90deg, #b08250 0%, #d9b885 18%, #ecd5aa 48%, #dfbf8d 74%, #a67a47 100%)",
  boxShadow: "inset 0 0 0 0.5px rgba(90,60,30,0.35)",
  pointerEvents: "none",
  // The stage reveals it once it has measured the lift.
  opacity: 0,
};

/**
 * Hidden behind the body at rest; the stage sets how far below the frame it
 * reaches. `widthPx` is in this image's own (pre-scale) pixels.
 */
function PuppetStick({
  centerPct,
  topPct,
  widthPx,
}: {
  centerPct: number;
  topPct: number;
  widthPx?: number;
}) {
  return (
    <div
      data-puppet-stick
      aria-hidden
      style={{
        ...STICK_STYLE,
        ...(widthPx !== undefined && widthPx > 0
          ? {
              width: `${widthPx.toFixed(2)}px`,
              marginLeft: `${(-widthPx / 2).toFixed(2)}px`,
            }
          : null),
        left: `${centerPct.toFixed(4)}%`,
        top: `${topPct.toFixed(4)}%`,
        height: `calc(${(100 - topPct).toFixed(4)}% + var(--puppet-stick-below, 0px))`,
      }}
    />
  );
}

interface PersonaImageProps {
  image: PersonaImageInfo | null;
  className?: string;
  alt?: string;
  preferSmall?: boolean;
  /**
   * Which way the pose should face on screen. A pose whose
   * `panel.orientation` is "left" or "right" but doesn't match is mirrored
   * horizontally; poses without an orientation (or "center") never mirror.
   */
  preferredOrientation?: Exclude<PoseOrientation, "center">;
  /**
   * Extra size multiplier on top of the image's own `scale`, for a caller that
   * wants this instance bigger or smaller than the character normally is.
   */
  scale?: number;
}

/**
 * PersonaImage component that displays either a regular image or a panel from a paneled image.
 * Takes a PersonaImageInfo object that contains the imageRef and variationId.
 */
export function PersonaImage({
  image,
  className,
  alt = "Character",
  preferSmall = false,
  preferredOrientation,
  scale = 1,
}: PersonaImageProps) {
  const puppet = usePuppetStick();
  const edge = puppet ? paperEdgeFilter(puppet.edgePx) : undefined;
  const source = (
    image?.panel &&
      preferSmall &&
      image.imageRef?.small &&
      !image.imageRef.panels
      ? image.imageRef.small
      : image?.imageRef?.large
  );
  if (!image?.imageRef) {
    return null;
  }

  let { imageRef, panel } = image;

  /**
   * Relative sizing, pivoted on the BOTTOM CENTRE — the same point a stage
   * placement rotates about, and for the same reason: that point is where the
   * character's feet are. Scaling about the middle would move them off their
   * mark and out of any slot they were standing in, so a character would slide
   * as it grew.
   */
  const totalScale = (image.scale ?? 1) * scale;
  // Every stick the same width on stage: undo this image's own scale.
  const stickPx =
    puppet?.stickPx !== undefined && totalScale > 0
      ? puppet.stickPx / totalScale
      : undefined;

  const poseOrientation = panel?.orientation;
  const mirrored =
    preferredOrientation != null &&
    (poseOrientation === "left" || poseOrientation === "right") &&
    poseOrientation !== preferredOrientation;

  // convert from old formet.  TODO: remove when no longer needed
  if (panel && !panel.sprite) {
    const offsetY = ((panel as any).offsetY ||
      (panel as any).yOffset) as number;
    const size = 1024;
    const dim = size / panel.columns;
    panel = {
      ...panel,
      width: size,
      height: size,
      sprite: {
        left: dim * panel.column,
        top: dim * panel.row,
        width: dim,
        height: dim,
      },
      frame: {
        left: dim * panel.column,
        top: dim * panel.row - offsetY * dim,
        width: dim,
        height: dim,
      },
    };
  }

  // If we have panel, render as a panel
  if (panel?.sprite) {
    const {
      columns: totalColumns,
      rows: totalRows,
      sprite: bbox,
      frame: content,
      width: imageWidth,
      height: imageHeight,
    } = panel;
    const s = Math.max(1, content.width || content.height || 1);
    const pct = (n: number) => `${n.toFixed(6)}%`;

    const innerLeftPct = ((bbox.left - content.left) / s) * 100;
    const innerTopPct = ((bbox.top - content.top) / s) * 100;
    const innerWPct = (bbox.width / s) * 100;
    const innerHPct = (bbox.height / s) * 100;

    const safeBboxW = Math.max(1, bbox.width);
    const safeBboxH = Math.max(1, bbox.height);
    const bgSizeXPct = (imageWidth / safeBboxW) * 100;
    const bgSizeYPct = (imageHeight / safeBboxH) * 100;

    // For background-position with bg-size > element size:
    // bgLeft = -(bgW - elemW) * (pos%/100). We want bgLeft = -bbox.left * scale, which yields:
    // pos% = bbox.left / (imageWidth - bbox.width) * 100 (scale cancels out)
    const denomX = imageWidth - safeBboxW;
    const denomY = imageHeight - safeBboxH;
    const bgPosXPct = denomX !== 0 ? (bbox.left / denomX) * 100 : 0;
    const bgPosYPct = denomY !== 0 ? (bbox.top / denomY) * 100 : 0;

    return (
      <div
        className={cn("relative", className)}
        style={
          mirrored || totalScale !== 1
            ? {
                transform: [
                  totalScale !== 1 ? `scale(${totalScale})` : "",
                  mirrored ? "scaleX(-1)" : "",
                ]
                  .filter(Boolean)
                  .join(" "),
                transformOrigin: "50% 100%",
              }
            : undefined
        }
      >
        {puppet?.stick ? (
          <PuppetStick
            centerPct={innerLeftPct + innerWPct / 2}
            topPct={innerTopPct + innerHPct * 0.45}
            widthPx={stickPx}
          />
        ) : null}
        <div
          className={"absolute"}
          style={{
            filter: edge,
            backgroundImage: source ? `url(${source})` : undefined,
            left: `${pct(innerLeftPct)}`,
            top: `${pct(innerTopPct)}`,
            width: `${pct(innerWPct)}`,
            height: `${pct(innerHPct)}`,
            backgroundSize: `${pct(bgSizeXPct)} ${pct(bgSizeYPct)}`,
            backgroundPosition: `${pct(bgPosXPct)} ${pct(bgPosYPct)}`,
          }}
          role="img"
          aria-label={alt}
        />
      </div>
    );
  }

  // Regular image display
  if (puppet)
    return (
      <div
        className={cn("relative w-full h-full", className)}
        style={
          totalScale !== 1
            ? { transform: `scale(${totalScale})`, transformOrigin: "50% 100%" }
            : undefined
        }
      >
        {puppet.stick ? (
          <PuppetStick centerPct={50} topPct={55} widthPx={stickPx} />
        ) : null}
        <img
          src={source}
          alt={alt}
          className="relative w-full h-full object-contain object-bottom"
          style={{ filter: edge }}
        />
      </div>
    );
  return (
    <img
      src={source}
      alt={alt}
      className={cn("w-full h-full", className)}
      style={
        totalScale !== 1
          ? { transform: `scale(${totalScale})`, transformOrigin: "50% 100%" }
          : undefined
      }
    />
  );
}
